import { ReactNode, useEffect, useState } from 'react';
import { Loading } from '@trukt/ui/Loading';
import { useAccount, useMsal } from '@azure/msal-react';

import { MsalInstance } from '../MsalInstance';

interface MsalTokenGuardProps {
  children: ReactNode;
}

/**
 * This component ensures that the app's UI is not presented to the user
 * until their tokens have been successfully validated. Displaying the UI
 * only to log the user out moments later would feel like a bug.
 */
export const MsalTokenGuard = ({ children }: MsalTokenGuardProps) => {
  const [isLoading, setIsLoading] = useState(true);

  const { instance, accounts } = useMsal();

  const extendedInstance = instance as MsalInstance;

  const account = useAccount(accounts[0]);

  const handleValidateToken = async () => {
    if (!account) {
      await instance.logoutRedirect({ account });
      return;
    }

    instance
      .acquireTokenSilent(extendedInstance.getSilentRequest(account))
      .then(() => {
        setIsLoading(false);
      })
      // we are not setting loading to false in the catch or the finally because we do
      // not want the user to see the UI for a split second. They should see a loading indicator
      .catch(async () => {
        await instance.logoutRedirect({ account });
      });
  };

  useEffect(() => {
    handleValidateToken();
  }, [account]);

  if (isLoading) {
    return <Loading fullscreen />;
  }

  return <>{children}</>;
};
